import React from 'react';
import { Form, Radio, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { fieldLabel } from './FieldLabel';

/**
 * Responsabilité d'une personne dans l'accident - victime ou partie adverse.
 *
 * Question métier : « quelle part de la faute porte cette personne ? ». Un piéton qui
 * traverse hors passage peut en porter une part ; un passager, jamais.
 *
 * Le pourcentage est porté par `tauxRc` sur la fiche, et il désigne la part de
 * responsabilité DE LA PERSONNE elle-même - pas celle de l'assuré :
 *   - null → pas encore renseigné ;
 *   - 0    → non responsable ;
 *   - p    → responsable à p %.
 *
 * Barème du 2026-09-15 : la responsabilité se partage par quarts (0, 25, 50, 75,
 * 100 %), elle ne se saisit plus librement. Le serveur refuse toute autre valeur
 * nouvelle ; une valeur ancienne hors barème reste affichée pour ce qu'elle est.
 *
 * La question « est-elle responsable ? » reste posée d'abord (demande du même jour) :
 * Non vaut 0 %, Oui ouvre le choix du taux parmi 25, 50, 75 et 100 %.
 * `estResponsable` n'est qu'un état d'écran : les formulaires envoient `tauxRc`
 * calculé par `tauxRcDepuisSaisie`, et posent `estResponsable` à l'ouverture avec
 * `responsabiliteDepuisTaux`.
 */
export const TAUX_RC = [0, 25, 50, 75, 100];

export const OPTIONS_TAUX_RC = TAUX_RC.map((t) => ({ value: t, label: `${t} %` }));

export const estTauxRcDuBareme = (taux) =>
  taux !== null && taux !== undefined && taux !== '' && TAUX_RC.includes(Number(taux));

/* Taux proposés après un « Oui » : 0 % est la réponse « Non ». */
export const TAUX_RC_RESPONSABLE = TAUX_RC.filter((t) => t > 0);

export const responsabiliteDepuisTaux = (taux) => {
  if (taux === null || taux === undefined || taux === '') return undefined;
  return Number(taux) > 0 ? 'OUI' : 'NON';
};

export const tauxRcDepuisSaisie = ({ estResponsable, tauxRc } = {}) => {
  if (estResponsable === 'NON') return 0;
  if (estResponsable === 'OUI') return tauxRc === null || tauxRc === undefined || tauxRc === '' ? null : Number(tauxRc);
  return null;
};

/** Libellé court pour les listes : « Non responsable », « Responsable à 25 % », ou null. */
export const libelleResponsabilite = (taux) => {
  if (taux === null || taux === undefined || taux === '') return null;
  const n = Number(taux);
  return n > 0 ? i18n.t('commun:champResponsabilite.responsableA', { taux: n }) : i18n.t('commun:champResponsabilite.nonResponsable');
};

/** Taux du barème en boutons (`valeurs` : tout le barème par défaut). Hors barème : aucun actif. */
export function SelecteurTauxRc({ value, onChange, disabled, valeurs = TAUX_RC }) {
  return (
    <Radio.Group
      optionType="button"
      buttonStyle="solid"
      value={estTauxRcDuBareme(value) ? Number(value) : undefined}
      onChange={(e) => onChange?.(e.target.value)}
      options={valeurs.map((t) => ({ value: t, label: `${t} %` }))}
      disabled={disabled}
    />
  );
}

export default function ChampResponsabilite({ form, sujet: sujetFourni }) {
  const { t } = useTranslation('commun');
  const sujet = sujetFourni === undefined ? t('champResponsabilite.sujetParDefaut') : sujetFourni;
  const estResponsable = Form.useWatch('estResponsable', form);
  const tauxRc = Form.useWatch('tauxRc', form);
  const horsBareme = estResponsable === 'OUI' && tauxRc !== null && tauxRc !== undefined && tauxRc !== ''
    && !TAUX_RC_RESPONSABLE.includes(Number(tauxRc));

  const surChoix = (e) => {
    const choix = e.target.value;
    if (choix === 'NON') form.setFieldValue('tauxRc', 0);
    // Passer à Oui depuis « non responsable » : le 0 ne vaut pas un taux choisi.
    if (choix === 'OUI' && !(Number(form.getFieldValue('tauxRc')) > 0)) form.setFieldValue('tauxRc', undefined);
  };

  return (
    <Row gutter={16} align="top">
      <Col xs={24} sm={9}>
        <Form.Item name="estResponsable" label={fieldLabel(t('champResponsabilite.question', { sujet, sujetMajuscule: `${sujet.charAt(0).toUpperCase()}${sujet.slice(1)}` }))}>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            onChange={surChoix}
            options={[{ value: 'NON', label: t('champResponsabilite.non') }, { value: 'OUI', label: t('champResponsabilite.oui') }]}
          />
        </Form.Item>
      </Col>
      {estResponsable === 'OUI' && (
        <Col xs={24} sm={15}>
          <Form.Item
            name="tauxRc"
            label={fieldLabel(t('champResponsabilite.partResponsabilite'), true)}
            rules={[{ required: true, message: t('champResponsabilite.choisissezTaux') }]}
            extra={horsBareme
              ? <span style={{ fontSize: 11.5, color: '#b45309' }}>{t('champResponsabilite.horsBareme', { taux: Number(tauxRc) })}</span>
              : null}
          >
            <SelecteurTauxRc valeurs={TAUX_RC_RESPONSABLE} />
          </Form.Item>
        </Col>
      )}
    </Row>
  );
}
